import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import type { Payment } from "@/features/payments/services/paymentsApi";

interface PrintReceiptDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoiceNumber?: string;
  payments: Payment[];
  isSubmitting?: boolean;
  onSubmit: (payload: {
    paymentId: string;
    isReprint: boolean;
    reprintReason?: string;
    ipAddress?: string;
  }) => void;
}

export const PrintReceiptDialog = ({
  open,
  onOpenChange,
  invoiceNumber,
  payments,
  isSubmitting = false,
  onSubmit,
}: PrintReceiptDialogProps) => {
  const [paymentId, setPaymentId] = useState("");
  const [isReprint, setIsReprint] = useState("false");
  const [reprintReason, setReprintReason] = useState("");
  const [ipAddress, setIpAddress] = useState("");

  const selectedPayment = payments.find((p) => p.id === paymentId);
  const alreadyPrinted = !!selectedPayment?.receiptNumber;

  useEffect(() => {
    if (!open) {
      return;
    }

    const firstPayment = payments[0];
    setPaymentId(firstPayment?.id ?? "");
    setIsReprint(firstPayment?.receiptNumber ? "true" : "false");
    setReprintReason("");
    setIpAddress("");
  }, [open, payments]);

  // Keep isReprint in sync when the user switches to a payment that was already printed.
  useEffect(() => {
    if (alreadyPrinted) {
      setIsReprint("true");
    }
  }, [alreadyPrinted]);

  const handleSubmit = () => {
    if (!paymentId) {
      return;
    }

    const reprint = isReprint === "true";
    onSubmit({
      paymentId,
      isReprint: reprint,
      reprintReason: reprint ? reprintReason.trim() || undefined : undefined,
      ipAddress: ipAddress.trim() || undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Print Receipt</DialogTitle>
          <DialogDescription>
            Print receipt for {invoiceNumber ?? "selected invoice"} using the related payment record.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Payment</Label>
            <Select value={paymentId} onValueChange={setPaymentId}>
              <SelectTrigger>
                <SelectValue placeholder="Select payment" />
              </SelectTrigger>
              <SelectContent>
                {payments.map((payment) => (
                  <SelectItem key={payment.id} value={payment.id}>
                    {payment.paymentNumber} - {new Intl.NumberFormat("en-KE", { style: "currency", currency: "KES" }).format(payment.amount)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Print Type</Label>
            <Select value={isReprint} onValueChange={setIsReprint} disabled={alreadyPrinted}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="false">First print</SelectItem>
                <SelectItem value="true">Reprint</SelectItem>
              </SelectContent>
            </Select>
            {alreadyPrinted && (
              <p className="text-xs text-muted-foreground">
                This payment already has receipt {selectedPayment!.receiptNumber} — must be a reprint.
              </p>
            )}
          </div>

          {isReprint === "true" && (
            <div className="space-y-2">
              <Label>Reprint Reason</Label>
              <Textarea
                value={reprintReason}
                onChange={(event) => setReprintReason(event.target.value)}
                placeholder="Reason for reprinting"
              />
            </div>
          )}

          <div className="space-y-2">
            <Label>IP Address (optional)</Label>
            <Input
              value={ipAddress}
              onChange={(event) => setIpAddress(event.target.value)}
              placeholder="e.g. 192.168.1.10"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !paymentId || (isReprint === "true" && !reprintReason.trim())}
          >
            {isSubmitting ? "Printing..." : "Print Receipt"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
